import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoArrowBack } from 'react-icons/io5';
import { LuShare } from 'react-icons/lu';
import { PiNotePencilBold } from "react-icons/pi";
import GalleryGrid from './GalleryGrid';
import OpenGalleryModal from './OpenGalleryModal';
import MapComponent from '../../Manager/Components/MapComponent';
import Review from './Review';
import { fetchRestaurantById, increaseViewCount } from '../../../lib/api/restaurant.apis';
import type { Restaurant } from '../../Manager/restaurant.types';
import OpenHours, { getOpenStatus } from './OpeningHours';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import RestaurantContact from './Contact';
import MapOverlay from '../../MapOverlay';
import { TbMapPin } from 'react-icons/tb';
import SaveButton from '../../Components/SaveButton';
import type { GalleryItem as GI } from '../../Manager/restaurant.types';
import { trackView } from '../../../lib/api/feed.apis';
import { Helmet } from "react-helmet";
import { BadgeCheck, ChevronDown, ChevronUp, Eye, Star, MapPin, Clock, Users, Utensils, Phone, Share2, BookOpen } from 'lucide-react';
import SuggestEdit from '../Components/SuggestEdit';
import GalleryPlaceholder from '../Components/GalleryPlaceHolder';
import ReservationPopup from './ReservationPopup';
import { useAuth } from '../../../Context/AuthContext';
import SuggestEditModal from './SuggestEditModal';

interface GalleryItem extends GI {
  url: string;
  publicId?: string;
  label?: string;
  id: string;
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

const Shimmer = ({ className }: { className?: string }) => (
  <div className={`bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:400%_100%] animate-[shimmer_1.4s_ease_infinite] rounded-lg ${className}`}
    style={{ backgroundImage: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite' }}
  />
);

const LoadingSkeleton = () => (
  <div className="min-h-screen bg-white">
    <style>{`@keyframes shimmer { 0%{background-position:100% 50%} 100%{background-position:0% 50%} }`}</style>
    {/* Top bar */}
    <div className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-100 flex items-center px-4 gap-3 z-50">
      <Shimmer className="w-8 h-8 rounded-full" />
      <Shimmer className="w-32 h-4" />
      <div className="ml-auto flex gap-3">
        <Shimmer className="w-8 h-8 rounded-full" />
        <Shimmer className="w-8 h-8 rounded-full" />
        <Shimmer className="w-8 h-8 rounded-full" />
      </div>
    </div>
    {/* Hero */}
    <div className="pt-14">
      <Shimmer className="w-full h-[45vw] min-h-[260px] max-h-[520px] rounded-none" />
    </div>
    {/* Content */}
    <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-6">
        <div className="space-y-3">
          <Shimmer className="w-2/3 h-8" />
          <Shimmer className="w-1/3 h-5" />
          <Shimmer className="w-1/2 h-4" />
        </div>
        <div className="space-y-2 pt-4">
          {[...Array(4)].map((_, i) => <Shimmer key={i} className="w-full h-4" />)}
        </div>
        <div className="flex gap-2 pt-2">
          {[...Array(5)].map((_, i) => <Shimmer key={i} className="w-20 h-8 rounded-full" />)}
        </div>
      </div>
      <div className="space-y-4">
        <Shimmer className="w-full h-36 rounded-2xl" />
        <Shimmer className="w-full h-56 rounded-2xl" />
      </div>
    </div>
  </div>
);

// ─── Pill / Tag chip ─────────────────────────────────────────────────────────

const Chip = ({ label }: { label: string }) => (
  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700 text-sm font-medium whitespace-nowrap hover:bg-gray-100 transition-colors">
    {label}
  </span>
);

// ─── Status dot ──────────────────────────────────────────────────────────────

const openColor = (status: string) => {
  if (!status) return '#9ca3af';
  const s = status.toLowerCase();
  if (s.startsWith('open')) return '#16a34a';
  if (s.startsWith('closed, opens')) return '#d97706';
  return '#dc2626';
};

// ─── Section wrapper ─────────────────────────────────────────────────────────

const Section = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`py-6 border-b border-gray-100 last:border-0 ${className}`}>{children}</div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-semibold text-gray-900 mb-4">{children}</h2>
);

// ─── Info row ────────────────────────────────────────────────────────────────

const InfoPill = ({
  icon,
  label,
  onClick,
  underline,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  underline?: boolean;
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors ${underline ? 'underline underline-offset-2' : ''}`}
  >
    <span className="text-gray-400">{icon}</span>
    {label}
  </button>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const RestaurantDetailsPage = () => {
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);
  const [showClaimTooltip, setShowClaimTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [reserveModal, setReserveModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const charsLimit = 220;
  const [openEditModal, setOpenEditModal] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoading(true);
    fetchRestaurantById(restaurantId).then((data) => {
      setRestaurant(data ?? null);
      setIsLoading(false);
      increaseViewCount(restaurantId!);
      trackView(restaurantId!);
    });
  }, []);

  // Compact header on scroll
  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(navigator.language || 'en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(amount)
      .replace('KES', 'Ksh');

  const getAllImages = (): GalleryItem[] => {
    if (!restaurant) return [];
    const images: GalleryItem[] = [];
    if (restaurant.gallery.cover) images.push({ ...restaurant.gallery.cover });
    restaurant.gallery.menu?.forEach((item) => images.push(item));
    restaurant.gallery.food.forEach((item) => images.push(item));
    restaurant.gallery.ambiance.forEach((item) => images.push(item));
    return images;
  };

  const openGallery = (index: number) => {
    setSelectedGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const getBudgetLabel = (level: string) => {
    const map: Record<string, string> = {
      $: 'Budget Friendly',
      $$: 'Moderate',
      $$$: 'Premium',
      $$$$: 'Fine Dining',
    };
    return map[level] || level;
  };

  const handleShare = () => {
    if (!restaurant) return;
    if (navigator.share) {
      navigator.share({ title: restaurant.name, text: restaurant.description, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const scrollToReviews = () => {
    document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const shortDescription = (desc: string) =>
    desc.length <= charsLimit || isExpanded ? desc : desc.slice(0, charsLimit) + '…';

  // ── Guards ──────────────────────────────────────────────────────────────────

  if (isLoading) return <LoadingSkeleton />;

  if (!restaurant)
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Utensils className="w-7 h-7 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Restaurant Not Found</h2>
          <p className="text-gray-500 text-sm mb-6">We couldn't load the restaurant details. It may have been removed or the link is incorrect.</p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2.5 bg-gray-900 text-white rounded-full text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );

  const images = getAllImages();
  const hasCover = !!restaurant?.gallery?.cover?.url;
  const googleImages: GalleryItem[] = (restaurant.googleMetadata?.photos ?? [])
    .filter((photo): photo is string => typeof photo === 'string' && photo.trim().length > 0)
    .map((photo, index) => ({
      id: `google-photo-${index}`,
      url: photo,
      label: `Google photo ${index + 1}`,
    }));
  const hasGooglePhotos = googleImages.length > 0;
  const displayImages = hasCover ? images : googleImages;
  const metadataRating = typeof restaurant.googleMetadata?.rating === 'number' ? restaurant.googleMetadata.rating : null;
  const metadataReviewCount = typeof restaurant.googleMetadata?.user_rating_count === 'number' ? restaurant.googleMetadata.user_rating_count : null;
  const overallRating = metadataRating ?? restaurant.rating.average ?? 0;
  const overallReviewCount = metadataReviewCount ?? restaurant.rating.count ?? 0;
  const ratingSource = metadataRating !== null ? 'Google' : 'Gemio';
  const openStatus = getOpenStatus(restaurant.openingHours);
  const claimStatus = String(restaurant?.claimStatus ?? 'unclaimed').toLowerCase();
  const hasReservations = restaurant?.allowOnlineReservations && user?.name;

  return (
    <>
      <Helmet>
        <title>{restaurant.name ? `${restaurant.name} – Gemio` : 'Gemio.'}</title>
        <meta
          name="description"
          content={restaurant.description ? restaurant.description.slice(0, 160) : 'Discover top restaurants on Gemio.'}
        />
      </Helmet>

      {/* ── Sticky Top Bar ─────────────────────────────────────────────────── */}
      <div
        ref={headerRef}
        className={`fixed top-17 left-0 right-0 z-40 bg-white transition-shadow duration-200 ${
          headerScrolled ? 'shadow-sm border-b border-gray-100' : 'border-b border-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 h-14">
          {/* Back */}
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-1.5 text-gray-700 hover:text-gray-900 transition-colors shrink-0"
          >
            <IoArrowBack className="w-5 h-5" />
            <span className={`text-sm font-medium transition-opacity duration-150 ${headerScrolled ? 'hidden sm:inline' : 'inline'}`}>
              Back
            </span>
          </button>

          {/* Restaurant name – appears on scroll */}
          <AnimatePresence>
            {headerScrolled && (
              <motion.p
                key="headerName"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
                className="font-semibold text-gray-900 text-sm truncate flex-1"
              >
                {restaurant.name}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={scrollToReviews}
              title="Write a review"
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <PiNotePencilBold className="w-5 h-5" />
            </button>
            <button
              onClick={handleShare}
              title="Share"
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <Share2 className="w-[18px] h-[18px]" />
            </button>
            <div className="w-9 h-9 flex items-center justify-center">
              <SaveButton restaurantId={restaurant._id} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Page Body ──────────────────────────────────────────────────────── */}
      <div className="min-h-screen bg-white pt-">

        {/* ── Gallery ──────────────────────────────────────────────────────── */}
        {(hasCover || hasGooglePhotos) ? (
          <GalleryGrid images={displayImages} openGallery={openGallery} />
        ) : (
          <GalleryPlaceholder restaurantName={restaurant.name} onSuggestEdit={() => setOpenEditModal(true)} />
        )}

        {/* ── Main Content Grid ─────────────────────────────────────────────── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-12">

            {/* ── Left / main column ─────────────────────────────────────── */}
            <div className="lg:col-span-2">

              {/* ── Hero info ──────────────────────────────────────────────── */}
              <Section>
                {/* Name + claim badge */}
                <div className="flex flex-wrap items-start gap-2 mb-3">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                    {restaurant.name}
                  </h1>

                  {/* Claim badge */}
                  {['unclaimed', 'pending', 'claimed'].includes(claimStatus) && (
                    <div className="relative mt-1">
                      <button
                        onClick={() => setShowClaimTooltip((v) => !v)}
                        className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${
                          claimStatus === 'claimed'
                            ? 'bg-green-50 border-green-300 text-green-700'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {claimStatus === 'claimed' ? (
                          <><BadgeCheck className="w-3 h-3" /> Verified</>
                        ) : (
                          'Unclaimed'
                        )}
                      </button>
                      <AnimatePresence>
                        {showClaimTooltip && (
                          <motion.div
                            initial={{ opacity: 0, y: 4, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 4, scale: 0.97 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-4 z-30 text-sm"
                          >
                            {claimStatus !== 'claimed' ? (
                              <>
                                <p className="font-semibold text-gray-900 mb-1">This business is unclaimed.</p>
                                <p className="text-gray-500 mb-3 text-xs leading-relaxed">
                                  Owners can update listing info, add photos, and respond to reviews.
                                </p>
                                <button
                                  onClick={() => navigate(`/restaurants/${restaurant._id}/claim`)}
                                  className="text-[#ee5219] hover:underline font-medium text-xs"
                                >
                                  Claim your free listing →
                                </button>
                              </>
                            ) : (
                              <>
                                <p className="text-gray-500 mb-3 text-xs leading-relaxed">
                                  This business is managed by another account.
                                </p>
                                <button
                                  onClick={() => navigate(`/restaurants/${restaurant._id}/transfer`)}
                                  className="text-[#ee5219] hover:underline font-medium text-xs"
                                >
                                  Request ownership →
                                </button>
                              </>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Suggest edit (when no cover) */}
                  {(!hasCover && !hasGooglePhotos) && <div className="mt-1"><SuggestEdit onSuggestEdit={() => setOpenEditModal(true)} /></div>}
                </div>

                {/* Rating + quick facts row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 mb-4">
                  {/* Rating */}
                  <div className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-semibold text-gray-900">{overallRating.toFixed(1)}</span>
                    {overallReviewCount ? (
                      <button onClick={scrollToReviews} className="underline underline-offset-2 hover:text-gray-900 transition-colors">
                        {overallReviewCount.toLocaleString()} {ratingSource} reviews
                      </button>
                    ) : null}
                  </div>

                  <span className="text-gray-300">·</span>

                  {/* Cuisine */}
                  <span className="text-gray-700">{restaurant.cuisine?.join(', ') || 'International'}</span>

                  <span className="text-gray-300">·</span>

                  {/* Budget */}
                  <span className="text-gray-700">{getBudgetLabel(restaurant.budgetLevel)} <span className="text-gray-400">({restaurant.budgetLevel})</span></span>

                  <span className="text-gray-300">·</span>

                  {/* Price */}
                  <span>From <span className="font-medium text-gray-900">{formatCurrency(restaurant.priceRange.min)}</span></span>
                </div>

                {/* Status + address row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                  {/* Open status */}
                  {restaurant.openingHours && (
                    <span
                      className="flex items-center gap-1.5 font-medium"
                      style={{ color: openColor(openStatus) }}
                    >
                      <span
                        className="w-2 h-2 rounded-full inline-block"
                        style={{ backgroundColor: openColor(openStatus) }}
                      />
                      {openStatus}
                    </span>
                  )}

                  {/* Address */}
                  <button
                    onClick={() => setShowMap(true)}
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-900 underline underline-offset-2 transition-colors"
                  >
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    {restaurant.location.address}
                  </button>
                </div>

                {/* Contact */}
                <div className="mt-4">
                  <RestaurantContact contactInfo={restaurant?.contactInfo} />
                </div>
              </Section>

              {/* ── Description ────────────────────────────────────────────── */}
              <Section>
                <SectionTitle>About</SectionTitle>
                <p className="text-gray-600 leading-relaxed text-[15px]">
                  {shortDescription(restaurant.description)}
                </p>
                {restaurant.description.length > charsLimit && (
                  <button
                    onClick={() => setIsExpanded((v) => !v)}
                    className="mt-2 flex items-center gap-1 text-sm font-medium text-gray-900 underline underline-offset-2 hover:text-gray-600 transition-colors"
                  >
                    {isExpanded ? (
                      <><ChevronUp className="w-4 h-4" /> Show less</>
                    ) : (
                      <><ChevronDown className="w-4 h-4" /> Read more</>
                    )}
                  </button>
                )}
              </Section>

              {/* ── Experience & Features ───────────────────────────────────── */}
              {(restaurant.experience.length > 0 || restaurant.tags.length > 0) && (
                <Section>
                  <SectionTitle>What to expect</SectionTitle>
                  <div className="space-y-5">
                    {restaurant.experience.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2.5">Experience</p>
                        <div className="flex flex-wrap gap-2">
                          {restaurant.experience.map((item, i) => <Chip key={i} label={item} />)}
                        </div>
                      </div>
                    )}
                    {restaurant.tags.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2.5">Offered here</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4">
                          {restaurant.tags.map((tag, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                              <span className="w-4 h-4 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                                <span className="text-green-600 text-[10px] font-bold">✓</span>
                              </span>
                              {tag}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* ── Location ───────────────────────────────────────────────── */}
              <Section>
                <div className="flex relative items-center justify-between mb-1">
                  <SectionTitle>Location</SectionTitle>
                  <button
                    onClick={() => setShowMap((v) => !v)}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    <TbMapPin className="w-4 h-4" />
                    View on map
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {[restaurant.location.address, restaurant.location.area, restaurant.location.city]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                <div
                  className="rounded-2xl overflow-hidden h-56 sm:h-72 border border-gray-100 cursor-pointer page-map"
                  onClick={() => setShowMap(true)}
                >
                  <MapComponent
                    locationData={{
                      lng: restaurant.location.geo.coordinates[0],
                      lat: restaurant.location.geo.coordinates[1],
                    }}
                    height="100%"
                    searchable={false}
                    interactive={false}
                  />
                </div>
              </Section>

              {/* ── Reviews ────────────────────────────────────────────────── */}
              <div id="reviews-section" />
              <Review restaurant={restaurant} />
            </div>

            {/* ── Right / sidebar ────────────────────────────────────────── */}
            <div className="lg:col-span-1 mt-6 lg:mt-0">
              <div className="lg:sticky lg:top-20 space-y-4">

                {/* CTA card – Reserve or Save */}
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  {restaurant?.claimStatus === "claimed" && hasReservations ? (
                    <div className="p-5">
                      <p className="text-base font-semibold text-gray-900 mb-1">Reserve a table</p>
                      <p className="text-sm text-gray-500 mb-4">
                        Book directly with {restaurant.name}
                      </p>
                      <button
                        onClick={() => setReserveModal(true)}
                        className="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 active:scale-[0.98] transition-all"
                      >
                        Reserve a Table
                      </button>
                    </div>
                  ) : (
                    <div className="p-5">
                      <p className="text-base font-semibold text-gray-900 mb-1">Save this restaurant</p>
                      <p className="text-sm text-gray-500 mb-4">
                        Add {restaurant.name} to your saved list
                      </p>
                      <div className="w-full">
                        <SaveButton restaurantId={restaurant._id} variant="full" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Opening hours card */}
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <p className="text-sm font-semibold text-gray-900">Opening Hours</p>
                    </div>
                    <OpenHours openingHours={restaurant.openingHours} />
                  </div>
                </div>

                {/* Views badge */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400 py-2">
                  <Eye className="w-3.5 h-3.5" />
                  <span>{restaurant.viewCount.toLocaleString()} people viewed this restaurant</span>
                </div>

                {/* Write a review shortcut */}
                <button
                  onClick={scrollToReviews}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <PiNotePencilBold className="w-4 h-4" />
                  Write a review
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile floating action bar ─────────────────────────────────────── */}
      {/* Shows on mobile only when scrolled past the gallery */}
      <AnimatePresence>
        {headerScrolled && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-100 shadow-lg px-4 py-3 z-40 safe-area-bottom"
          >
            <div className="flex gap-3 max-w-sm mx-auto">
              {hasReservations ? (
                <button
                  onClick={() => setReserveModal(true)}
                  className="flex-1 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 active:scale-[0.98] transition-all"
                >
                  Reserve a Table
                </button>
              ) : (
                <div className="flex-1">
                  <SaveButton restaurantId={restaurant._id} variant="full" />
                </div>
              )}
              <button
                onClick={scrollToReviews}
                className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <PiNotePencilBold className="w-4 h-4" />
                Review
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {openEditModal && (
          <SuggestEditModal 
           restaurant={restaurant}
            onClose={() => setOpenEditModal(false)}
          />
        )}
      </AnimatePresence>


      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      <MapOverlay show={showMap} onClose={() => setShowMap(false)} restaurants={[restaurant]} />

      {reserveModal && (
        <ReservationPopup
          restaurantId={restaurant._id}
          onSuccess={() => setReserveModal(false)}
          onClose={() => setReserveModal(false)}
        />
      )}

      <OpenGalleryModal
        name={restaurant.name}
        images={displayImages}
        isGalleryOpen={isGalleryOpen}
        setIsGalleryOpen={setIsGalleryOpen}
        restaurantId={restaurant._id}
      />
    </>
  );
};

export default RestaurantDetailsPage;